import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { AnswerExplanationEditor } from "../../components/AnswerExplanationEditor";
import { TagPicker } from "../../components/TagPicker";
import { listCompanies, type Company } from "../../services/companies-api";
import { getAppCatalog, normalizeTag, type AppCatalog } from "../../services/app-catalog-api";
import type {
  LanguageTag,
  PositionType,
  Question,
  QuestionInput,
  QuestionType,
  QuestionVisibility,
} from "../../services/questions-api";

const LANGUAGES: { value: LanguageTag; label: string }[] = [
  { value: "en", label: "English" },
  { value: "vi", label: "Vietnamese" },
  { value: "km", label: "Khmer" },
];

/** FR-055: the three organisational levels, in the order the product owner lists them. */
const POSITION_TYPES: { value: PositionType; label: string }[] = [
  { value: "employee", label: "Employee" },
  { value: "manager", label: "Manager" },
  { value: "bod", label: "BOD" },
];

const TYPES: { value: QuestionType; label: string }[] = [
  { value: "single_choice", label: "Single choice" },
  { value: "multi_select", label: "Multi-select" },
  { value: "true_false", label: "True / False" },
  { value: "free_text", label: "Free text (manually graded)" },
];

function emptyForm(): QuestionInput {
  return {
    language: "en",
    text: "",
    type: "single_choice",
    options: ["", ""],
    correctAnswer: [],
    explanationHtml: "",
    appTags: [],
    visibility: "global",
    companyGrantIds: [],
    isFeedback: false,
    targetPositionTypes: [],
  };
}

/** FR-010/FR-025/FR-028/FR-040: create/edit form covering every question type's fields, the
 * rich-text answer explanation, app tags, the Language tag, and the company-specific picker. */
export function QuestionForm({
  initial,
  onSubmit,
  submitting,
}: {
  initial?: Question;
  onSubmit: (input: QuestionInput) => void;
  submitting: boolean;
}) {
  const { t } = useTranslation();
  const [form, setForm] = useState<QuestionInput>(
    initial
      ? {
          language: initial.language,
          text: initial.text,
          type: initial.type,
          options: initial.options ?? ["", ""],
          correctAnswer: initial.correctAnswer ?? [],
          explanationHtml: initial.explanationHtml,
          appTags: initial.appTags,
          isFeedback: initial.isFeedback,
          targetPositionTypes: initial.targetPositionTypes,
          visibility: initial.visibility,
          companyGrantIds: initial.companyGrantIds,
        }
      : emptyForm(),
  );
  const [companies, setCompanies] = useState<Company[]>([]);
  /** Which sections failed validation on the last save attempt (FR-045). Empty until the CO
   * Member actually presses save, so the form doesn't show red before they've done anything. */
  const [invalidSections, setInvalidSections] = useState<Set<string>>(new Set());
  const [catalog, setCatalog] = useState<AppCatalog | null>(null);
  /** Distinguishes "the catalogue fetch failed" from "it hasn't arrived yet" — identical from
   * `catalog === null`, but they need different messages. */
  const [catalogFailed, setCatalogFailed] = useState(false);
  /** FR-070: tags that aren't Base apps. Deliberately held outside `form.appTags`, which is what
   * gets submitted — that's what makes "the wrong tag will not be saved once the CO Member
   * presses Save changes" true by construction rather than by remembering to filter. */
  const [rejectedTags, setRejectedTags] = useState<string[]>([]);

  useEffect(() => {
    if (form.visibility === "company_specific") {
      listCompanies()
        .then(setCompanies)
        .catch(() => setCompanies([]));
    }
  }, [form.visibility]);

  // The tag catalogue (T128). Once it arrives, the tags already stored on the question are run
  // through it too: case variants are corrected in place ("WORKFLOW" → "Workflow"), and tags
  // that predate the catalogue and match nothing are moved into the red rejected list so the CO
  // Member can see them and drop them (FR-070 — the product owner will reassign those by hand).
  useEffect(() => {
    getAppCatalog()
      .then((loaded) => {
        setCatalog(loaded);
        const valid: string[] = [];
        const refused: string[] = [];
        // `initial` is the question as loaded; the CO Member can't have edited tags yet, because
        // the picker is disabled-in-effect until the catalogue it validates against exists.
        for (const tag of initial?.appTags ?? []) {
          const canonical = normalizeTag(tag, loaded.allApps);
          if (canonical) {
            if (!valid.includes(canonical)) valid.push(canonical);
          } else if (!refused.includes(tag)) {
            refused.push(tag);
          }
        }
        if (initial) {
          setForm((f) => ({ ...f, appTags: valid }));
          setRejectedTags(refused);
        }
      })
      .catch(() => {
        setCatalog(null);
        setCatalogFailed(true);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function update<K extends keyof QuestionInput>(key: K, value: QuestionInput[K]) {
    setForm((f) => ({ ...f, [key]: value }));
  }

  function toggleCompanyGrant(companyId: string) {
    const ids = form.companyGrantIds ?? [];
    update(
      "companyGrantIds",
      ids.includes(companyId) ? ids.filter((id) => id !== companyId) : [...ids, companyId],
    );
  }

  const options = form.options ?? [];
  const correctAnswer = form.correctAnswer ?? [];
  const isFeedback = form.isFeedback === true;
  const positionTypes = form.targetPositionTypes ?? [];

  function togglePositionType(value: PositionType) {
    update(
      "targetPositionTypes",
      positionTypes.includes(value)
        ? positionTypes.filter((p) => p !== value)
        : [...positionTypes, value],
    );
  }

  function toggleCorrectOption(option: string) {
    if (form.type === "single_choice") {
      update("correctAnswer", [option]);
    } else {
      update(
        "correctAnswer",
        correctAnswer.includes(option)
          ? correctAnswer.filter((o) => o !== option)
          : [...correctAnswer, option],
      );
    }
  }

  /** FR-045. The reported bug was "clicking Create question just does nothing": `Question text`
   * carries HTML `required`, but the app-tag chips and the correct-answer radios are custom
   * widgets the browser can't validate, so the form submitted, the backend rejected it with a
   * 400, and nothing surfaced. This validates the same rules the backend enforces
   * (question-service.ts's `validate`) *before* submitting, and names the offending sections. */
  function findInvalidSections(): Set<string> {
    const invalid = new Set<string>();
    if (!form.text.trim()) invalid.add("text");
    // FR-056: a Feedback question carries no app tag (it goes to every test regardless) and has
    // no right answer, so neither requirement applies to it.
    if (!isFeedback && form.appTags.length === 0) invalid.add("appTags");

    if (form.type === "single_choice" || form.type === "multi_select") {
      const filled = options.filter((o) => o.trim());
      if (filled.length < 2) invalid.add("options");
      if (!isFeedback && correctAnswer.length === 0) invalid.add("correctAnswer");
    }
    if (!isFeedback && form.type === "true_false" && correctAnswer.length === 0) {
      invalid.add("correctAnswer");
    }
    if (form.visibility === "company_specific" && (form.companyGrantIds ?? []).length === 0) {
      invalid.add("companyGrantIds");
    }
    return invalid;
  }

  /** Red outline for a section the CO Member left incomplete, applied only after they've actually
   * tried to save — flagging fields red before anyone has done anything wrong is just noise. */
  const outline = (key: string) =>
    invalidSections.has(key) ? "rounded border-2 border-accent p-2" : "";

  return (
    <form
      noValidate
      onSubmit={(e) => {
        e.preventDefault();
        const invalid = findInvalidSections();
        setInvalidSections(invalid);
        if (invalid.size > 0) return;
        onSubmit(form);
      }}
      className="flex max-w-2xl flex-col gap-4"
    >
      <label className="flex flex-col gap-1 text-sm text-body">
        Language tag
        <select
          value={form.language}
          onChange={(e) => update("language", e.target.value as LanguageTag)}
          className="rounded border border-surface-alt px-3 py-2"
        >
          {LANGUAGES.map((l) => (
            <option key={l.value} value={l.value}>
              {l.label}
            </option>
          ))}
        </select>
      </label>

      <label className={`flex flex-col gap-1 text-sm text-body ${outline("text")}`}>
        <span>
          Question text <span className="text-accent">*</span>
        </span>
        <textarea
          value={form.text}
          onChange={(e) => update("text", e.target.value)}
          className="rounded border border-surface-alt px-3 py-2"
          rows={3}
        />
        {invalidSections.has("text") && (
          <span className="text-xs text-accent">Question text is required.</span>
        )}
      </label>

      <label className="flex flex-col gap-1 text-sm text-body">
        Question type
        <select
          value={form.type}
          onChange={(e) => {
            const type = e.target.value as QuestionType;
            update("type", type);
            if (type === "true_false") {
              update("options", ["true", "false"]);
              update("correctAnswer", []);
            } else if (type === "free_text") {
              update("options", []);
              update("correctAnswer", []);
            } else if (!options.length) {
              update("options", ["", ""]);
            }
          }}
          className="rounded border border-surface-alt px-3 py-2"
        >
          {TYPES.map((t) => (
            <option key={t.value} value={t.value}>
              {t.label}
            </option>
          ))}
        </select>
      </label>

      {(form.type === "single_choice" || form.type === "multi_select") && (
        <div
          className={`flex flex-col gap-2 ${
            invalidSections.has("options") || invalidSections.has("correctAnswer")
              ? "rounded border-2 border-accent p-2"
              : ""
          }`}
        >
          <span className="text-sm text-body">
            Answer options <span className="text-accent">*</span>
            {isFeedback
              ? " — a Feedback question has no correct answer"
              : ` (check the correct ${form.type === "single_choice" ? "one" : "one or more"})`}
          </span>
          {options.map((option, i) => (
            <div key={i} className="flex items-center gap-2">
              {!isFeedback && (
                <input
                  type={form.type === "single_choice" ? "radio" : "checkbox"}
                  name="correct-option"
                  aria-label={`Option ${i + 1} is correct`}
                  checked={correctAnswer.includes(option)}
                  onChange={() => toggleCorrectOption(option)}
                  disabled={!option}
                />
              )}
              <input
                value={option}
                onChange={(e) => {
                  const next = [...options];
                  next[i] = e.target.value;
                  update("options", next);
                }}
                className="flex-1 rounded border border-surface-alt px-2 py-1"
                placeholder={`Option ${i + 1}`}
              />
              <button
                type="button"
                onClick={() => update("options", options.filter((_, idx) => idx !== i))}
                className="text-sm text-accent"
              >
                Remove
              </button>
            </div>
          ))}
          <button
            type="button"
            onClick={() => update("options", [...options, ""])}
            className="self-start text-sm text-brand"
          >
            + Add option
          </button>
          {invalidSections.has("options") && (
            <span className="text-xs text-accent">Provide at least two answer options.</span>
          )}
          {invalidSections.has("correctAnswer") && (
            <span className="text-xs text-accent">
              Mark which option is correct (tick the box beside it).
            </span>
          )}
        </div>
      )}

      {form.type === "true_false" && !isFeedback && (
        <div className={`flex flex-col gap-2 ${outline("correctAnswer")}`}>
          <span className="text-sm text-body">
            Correct answer <span className="text-accent">*</span>
          </span>
          <div className="flex gap-4">
          {["true", "false"].map((option) => (
            <label key={option} className="flex items-center gap-2 text-sm text-body">
              <input
                type="radio"
                name="correct-option"
                checked={correctAnswer[0] === option}
                onChange={() => update("correctAnswer", [option])}
              />
              {option === "true" ? "True" : "False"}
            </label>
          ))}
          </div>
          {invalidSections.has("correctAnswer") && (
            <span className="text-xs text-accent">Choose True or False as the correct answer.</span>
          )}
        </div>
      )}

      {form.type === "free_text" && (
        <p className="text-sm text-body">
          Free-text answers have no single correct value — a CO Member grades each one manually
          after submission (FR-012/FR-013).
        </p>
      )}

      {/* FR-056: a Feedback question collects opinions rather than testing knowledge. It is
          appended to every test regardless of tags, and never scored — so the Tags field and the
          correct-answer controls above disappear once this is ticked. */}
      <label className="flex items-start gap-2 rounded border border-surface-alt p-3 text-sm text-body">
        <input
          type="checkbox"
          checked={isFeedback}
          onChange={(e) => update("isFeedback", e.target.checked)}
          className="mt-0.5"
        />
        <span>
          <span className="text-ink">
            {t("questionForm.isFeedback", "This is a Feedback question (not scored)")}
          </span>
          <br />
          {t(
            "questionForm.isFeedbackHint",
            "Added to the end of every test, for any company. It needs no tag, has no correct answer, and never affects anyone's score.",
          )}
        </span>
      </label>

      {/* FR-055: empty = everyone, which is the default. */}
      <div className="flex flex-col gap-1">
        <span className="text-sm text-body">
          {t("questionForm.whoSees", "Who sees this question")}
        </span>
        <div className="flex flex-wrap gap-4">
          {POSITION_TYPES.map((p) => (
            <label key={p.value} className="flex items-center gap-2 text-sm text-body">
              <input
                type="checkbox"
                checked={positionTypes.includes(p.value)}
                onChange={() => togglePositionType(p.value)}
              />
              {t(`questionForm.position.${p.value}`, p.label)}
            </label>
          ))}
        </div>
        <span className="text-xs text-body">
          {positionTypes.length === 0
            ? t("questionForm.whoSeesAll", "Everyone — tick a level to restrict it.")
            : t(
                "questionForm.whoSeesWarning",
                "Only members set to these Position Types will see it. Set a member's Position Type on the company page or in the roster CSV.",
              )}
        </span>
      </div>

      {/* FR-070/FR-063: labelled "Tags", validated against the fixed Base app catalogue (T128).
          Everything about entry, normalisation and rejection lives in TagPicker, shared with the
          Company form so the two can't diverge again. Hidden entirely for Feedback questions,
          which bypass app-tag matching (FR-056). */}
      {/* Conditionally rendered rather than `hidden`: Tailwind's `flex` utility wins over
          Preflight's `[hidden]{display:none}`, so the attribute alone leaves it on screen. */}
      {!isFeedback && (
        <div className={`flex flex-col gap-1 ${outline("appTags")}`}>
          <TagPicker
            label="Tags"
            required
            inputId="question-app-tags"
            value={form.appTags}
            onChange={(tags) => update("appTags", tags)}
            rejected={rejectedTags}
            onRejectedChange={setRejectedTags}
            catalog={catalog}
            loadFailed={catalogFailed}
          />
          {invalidSections.has("appTags") && (
            <span className="text-xs text-accent">Add at least one tag.</span>
          )}
        </div>
      )}

      <div className="flex flex-col gap-1">
        <span className="text-sm text-body">Answer explanation</span>
        <AnswerExplanationEditor
          value={form.explanationHtml}
          onChange={(html) => update("explanationHtml", html)}
        />
      </div>

      <div className="flex flex-col gap-2">
        <span className="text-sm text-body">Visibility</span>
        <label className="flex items-center gap-2 text-sm">
          <input
            type="radio"
            checked={form.visibility === "global"}
            onChange={() => update("visibility", "global" as QuestionVisibility)}
          />
          Global — eligible for any company using its tagged app(s)
        </label>
        <label className="flex items-center gap-2 text-sm">
          <input
            type="radio"
            checked={form.visibility === "company_specific"}
            onChange={() => update("visibility", "company_specific" as QuestionVisibility)}
          />
          Company-specific (tailored) — pick which company/companies below
        </label>
        {form.visibility === "company_specific" && (
          <div className={`ml-6 flex flex-col gap-1 ${outline("companyGrantIds")}`}>
            {companies.map((company) => (
              <label key={company.id} className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={(form.companyGrantIds ?? []).includes(company.id)}
                  onChange={() => toggleCompanyGrant(company.id)}
                />
                {company.name}
              </label>
            ))}
            {companies.length === 0 && (
              <p className="text-sm text-body">No companies onboarded yet.</p>
            )}
            {invalidSections.has("companyGrantIds") && (
              <span className="text-xs text-accent">
                Select at least one company, or switch this question back to Global.
              </span>
            )}
          </div>
        )}
      </div>

      {/* A single summary above the button, so a CO Member who scrolled past a red section still
          understands why pressing save appeared to do nothing — the original reported bug. */}
      {invalidSections.size > 0 && (
        <p className="rounded border border-accent bg-surface-alt px-3 py-2 text-sm text-accent">
          Some required fields are missing — they're outlined in red above.
        </p>
      )}

      <button
        type="submit"
        disabled={submitting}
        className="self-start rounded bg-brand px-4 py-2 font-sans text-surface disabled:opacity-50"
      >
        {submitting ? "Saving…" : initial ? "Save changes" : "Create question"}
      </button>
    </form>
  );
}
